import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Plus, Trash2, Megaphone, Tag, Gift, Percent, Calendar as CalendarIcon, Timer, ShoppingCart, Users, Pencil } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { cn } from "../lib/utils";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";

const CHANNELS = [
  { value: "Retail", label: "Retail / Normal Trade" },
  { value: "GDO", label: "GDO" },
  { value: "DO", label: "DO" },
  { value: "Ho.Re.Ca.", label: "Ho.Re.Ca." },
  { value: "Distributore", label: "Distributore" },
];

const STATUS_LABELS = {
  draft: "Bozza",
  sent: "Inviato",
  accepted: "Accettato",
  rejected: "Rifiutato",
  expired: "Scaduto",
  converted: "Convertito",
};

// Simplified label for clarity
const NXM_LABEL = "NxM / Omaggio Quantità (es. 10+1)";

export default function PromosPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [promos, setPromos] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "discount", // discount, nxm, gift
    target_type: "product", // product, category, all
    target_ids: [],
    discount_type: "percent", // percent, fixed
    discount_value: 0,
    buy_qty: 0,
    get_qty: 0,
    gift_description: "",
    start_date: new Date(),
    end_date: new Date(new Date().setDate(new Date().getDate() + 30)),
    is_flash: false,
    active: true,
    valid_channels: [] // Empty = All
  });

  useEffect(() => {
    fetchPromos();
    if (isAdmin) {
      fetchProducts();
      fetchCategories();
    }
  }, []);

  const fetchPromos = async () => {
    try {
      const response = await api.get("/promos");
      setPromos(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle promozioni");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get("/products");
      setProducts(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get("/categories");
      setCategories(res.data);
    } catch (e) { console.error(e); }
  };

  const toggleChannel = (channel) => {
    setFormData(prev => {
      if (prev.valid_channels.includes(channel)) {
        return { ...prev, valid_channels: prev.valid_channels.filter(c => c !== channel) };
      } else {
        return { ...prev, valid_channels: [...prev.valid_channels, channel] };
      }
    });
  };

  const handleSubmit = async () => {
    if (!formData.title) return toast.error("Inserisci un titolo");
    
    try {
      const payload = {
        ...formData,
        target_ids: Array.isArray(formData.target_ids) ? formData.target_ids : [formData.target_ids],
        start_date: formData.start_date instanceof Date ? formData.start_date.toISOString() : formData.start_date,
        end_date: formData.end_date instanceof Date ? formData.end_date.toISOString() : formData.end_date
      };
      
      if (editingPromo) {
        await api.put(`/promos/${editingPromo.id}`, payload);
        toast.success("Promozione aggiornata!");
      } else {
        await api.post("/promos", payload);
        toast.success("Promozione creata!");
      }
      
      setDialogOpen(false);
      setEditingPromo(null);
      resetForm();
      fetchPromos();
    } catch (error) {
      toast.error(editingPromo ? "Errore nell'aggiornamento" : "Errore nella creazione");
    }
  };

  const handleEdit = (promo) => {
    setEditingPromo(promo);
    setFormData({
      title: promo.title,
      description: promo.description || "",
      type: promo.type,
      target_type: promo.target_type,
      target_ids: promo.target_ids || [],
      discount_type: promo.discount_type || "percent",
      discount_value: promo.discount_value || 0,
      buy_qty: promo.buy_qty || 0,
      get_qty: promo.get_qty || 0,
      gift_description: promo.gift_description || "",
      start_date: new Date(promo.start_date),
      end_date: new Date(promo.end_date),
      is_flash: promo.is_flash || false,
      active: promo.active !== undefined ? promo.active : true,
      valid_channels: promo.valid_channels || []
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      type: "discount",
      target_type: "product",
      target_ids: [],
      discount_type: "percent",
      discount_value: 0,
      buy_qty: 0,
      get_qty: 0,
      gift_description: "",
      start_date: new Date(),
      end_date: new Date(new Date().setDate(new Date().getDate() + 30)),
      is_flash: false,
      active: true,
      valid_channels: []
    });
  };

  const handleDelete = async (id) => {
    if (!confirm("Eliminare questa promo?")) return;
    try {
      await api.delete(`/promos/${id}`);
      toast.success("Promo eliminata");
      fetchPromos();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const formatDate = (dateStr) => {
    try {
        return format(new Date(dateStr), "d MMM", { locale: it });
    } catch { return ""; }
  };

  return (
    <div className="space-y-6 animate-fade-in p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-primary flex items-center gap-2">
            <Megaphone className="w-8 h-8 text-accent" />
            Promo Canvass
          </h1>
          <p className="text-muted-foreground">Offerte speciali e occasioni imperdibili</p>
        </div>
        
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingPromo(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-accent hover:bg-accent/90" onClick={() => { setEditingPromo(null); resetForm(); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nuova Promo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPromo ? "Modifica Promozione" : "Configura Nuova Promozione"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label>Titolo Promo</Label>
                        <Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="Es. Sconto Estivo" />
                    </div>
                    <div className="space-y-2">
                        <Label>Tipo Promo</Label>
                        <Select value={formData.type} onValueChange={v => setFormData({...formData, type: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="discount">Sconto</SelectItem>
                                <SelectItem value="nxm">{NXM_LABEL}</SelectItem>
                                <SelectItem value="gift">Omaggio (Gift)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label>Descrizione</Label>
                    <Input value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="Dettagli offerta..." />
                </div>

                {/* Target Channels */}
                <div className="space-y-2 border p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <Label className="flex items-center gap-2">
                        <Users className="w-4 h-4" /> 
                        Canali Validi (Lascia vuoto per TUTTI)
                    </Label>
                    <div className="flex flex-wrap gap-3 mt-2">
                        {CHANNELS.map(channel => (
                            <div key={channel.value} className="flex items-center space-x-2">
                                <Checkbox 
                                    id={`ch-${channel.value}`} 
                                    checked={formData.valid_channels.includes(channel.value)}
                                    onCheckedChange={() => toggleChannel(channel.value)}
                                />
                                <label htmlFor={`ch-${channel.value}`} className="text-sm cursor-pointer select-none">
                                    {channel.label}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 border p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <div className="space-y-2">
                        <Label>Applica a</Label>
                        <Select value={formData.target_type} onValueChange={v => setFormData({...formData, target_type: v, target_ids: []})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="product">Singolo Prodotto</SelectItem>
                                <SelectItem value="category">Categoria</SelectItem>
                                <SelectItem value="all">Tutto il Catalogo</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    
                    {formData.target_type !== 'all' && (
                        <div className="space-y-2">
                            <Label>Seleziona {formData.target_type === 'product' ? 'Prodotto' : 'Categoria'}</Label>
                            <Select onValueChange={v => setFormData({...formData, target_ids: [v]})}>
                                <SelectTrigger><SelectValue placeholder="Cerca..." /></SelectTrigger>
                                <SelectContent>
                                    {formData.target_type === 'product' ? 
                                        products.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>) :
                                        categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)
                                    }
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>

                {/* Conditional Logic Fields */}
                <div className="border p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <Label className="mb-2 block font-semibold text-accent">Regole Offerta</Label>
                    
                    {formData.type === 'discount' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Tipo Sconto</Label>
                                <Select value={formData.discount_type} onValueChange={v => setFormData({...formData, discount_type: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="percent">Percentuale (%)</SelectItem>
                                        <SelectItem value="fixed">Importo Fisso (€)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Valore</Label>
                                <Input type="number" value={formData.discount_value} onChange={e => setFormData({...formData, discount_value: parseFloat(e.target.value)})} />
                            </div>
                        </div>
                    )}

                    {(formData.type === 'nxm' || formData.type === 'gift') && (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Acquista (Qta)</Label>
                                <Input type="number" value={formData.buy_qty} onChange={e => setFormData({...formData, buy_qty: parseInt(e.target.value)})} />
                            </div>
                            <div className="space-y-2">
                                <Label>Ottieni in Omaggio (Qta)</Label>
                                <Input type="number" value={formData.get_qty} onChange={e => setFormData({...formData, get_qty: parseInt(e.target.value)})} />
                            </div>
                            {formData.type === 'gift' && (
                                <div className="col-span-2 space-y-2">
                                    <Label>Descrizione Omaggio</Label>
                                    <Input placeholder="Es. 6 Bicchieri, 1 Cavatappi..." value={formData.gift_description} onChange={e => setFormData({...formData, gift_description: e.target.value})} />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Timing */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 flex flex-col">
                        <Label>Inizio</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !formData.start_date && "text-muted-foreground")}>
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {formData.start_date ? format(formData.start_date, "PPP", { locale: it }) : <span>Seleziona</span>}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={formData.start_date} onSelect={d => setFormData({...formData, start_date: d})} /></PopoverContent>
                        </Popover>
                    </div>
                    <div className="space-y-2 flex flex-col">
                        <Label>Fine</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !formData.end_date && "text-muted-foreground")}>
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {formData.end_date ? format(formData.end_date, "PPP", { locale: it }) : <span>Seleziona</span>}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={formData.end_date} onSelect={d => setFormData({...formData, end_date: d})} /></PopoverContent>
                        </Popover>
                    </div>
                </div>
                
                <div className="flex items-center space-x-2">
                    <input type="checkbox" id="flash" checked={formData.is_flash} onChange={e => setFormData({...formData, is_flash: e.target.checked})} className="h-4 w-4" />
                    <Label htmlFor="flash" className="text-red-500 font-bold flex items-center gap-1"><Timer className="w-4 h-4"/> Flash Sale (Offerta a Tempo)</Label>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                    <Button onClick={handleSubmit} className="bg-accent">{editingPromo ? "Aggiorna Promo" : "Crea Promo"}</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Promos Grid */}
      {loading ? (
          <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div></div>
      ) : promos.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
              <Megaphone className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p>Nessuna promozione attiva al momento.</p>
          </div>
      ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {promos.map(promo => (
                  <Card key={promo.id} className={cn("overflow-hidden hover:shadow-lg transition-shadow", promo.is_flash && "border-2 border-red-500 shadow-red-100")}>
                      <div className={cn("h-3 w-full", promo.is_flash ? "bg-red-500" : "bg-accent")}></div>
                      <CardHeader>
                          <div className="flex justify-between items-start">
                              <div className="flex flex-col gap-2">
                                  {promo.is_flash && <Badge variant="destructive" className="w-fit animate-pulse"><Timer className="w-3 h-3 mr-1" /> FLASH SALE</Badge>}
                                  <Badge variant="outline" className="w-fit">
                                      {promo.type === 'discount' && <Percent className="w-3 h-3 mr-1" />}
                                      {promo.type === 'nxm' && <Tag className="w-3 h-3 mr-1" />}
                                      {promo.type === 'gift' && <Gift className="w-3 h-3 mr-1" />}
                                      {promo.type === 'discount' ? 'Sconto' : promo.type === 'nxm' ? 'Offerta NxM' : 'Omaggio'}
                                  </Badge>
                              </div>
                              {isAdmin && (
                                  <div className="flex gap-1">
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-600" onClick={() => handleEdit(promo)}>
                                          <Pencil className="w-4 h-4" />
                                      </Button>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(promo.id)}>
                                          <Trash2 className="w-4 h-4" />
                                      </Button>
                                  </div>
                              )}
                          </div>
                          <CardTitle className="text-xl">{promo.title}</CardTitle>
                          <CardDescription>{promo.description}</CardDescription>
                          
                          {/* Valid Channels Tags */}
                          {(promo.valid_channels && promo.valid_channels.length > 0) ? (
                              <div className="flex flex-wrap gap-1 mt-2">
                                  {promo.valid_channels.map(ch => (
                                      <span key={ch} className="text-[10px] bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded text-muted-foreground">
                                          {ch}
                                      </span>
                                  ))}
                              </div>
                          ) : (
                              <div className="mt-2 text-xs text-green-600 dark:text-green-400 font-medium">Valida per TUTTI i canali</div>
                          )}

                      </CardHeader>
                      <CardContent>
                          <div className="space-y-4">
                              <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg text-center">
                                  {promo.type === 'discount' && (
                                      <div className="text-3xl font-bold text-accent">
                                          -{promo.discount_value}{promo.discount_type === 'percent' ? '%' : '€'}
                                      </div>
                                  )}
                                  {promo.type === 'nxm' && (
                                      <div className="text-xl font-bold text-blue-600">
                                          Acquista {promo.buy_qty}, Paghi {promo.buy_qty - promo.get_qty}
                                          <div className="text-xs text-muted-foreground font-normal mt-1">({promo.get_qty} Gratis)</div>
                                      </div>
                                  )}
                                  {promo.type === 'gift' && (
                                      <div className="text-lg font-bold text-purple-600">
                                          Omaggio: {promo.gift_qty}x {promo.gift_description}
                                          <div className="text-xs text-muted-foreground font-normal mt-1">Ogni {promo.buy_qty} prodotti acquistati</div>
                                      </div>
                                  )}
                              </div>
                              
                              <div className="text-sm text-muted-foreground flex justify-between items-center border-t pt-2">
                                  <span>Valida fino al:</span>
                                  <span className="font-semibold text-foreground">{formatDate(promo.end_date)}</span>
                              </div>
                          </div>
                      </CardContent>
                      <CardFooter>
                          <Button className="w-full" onClick={() => navigate("/products")}>
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Vai ai Prodotti
                          </Button>
                      </CardFooter>
                  </Card>
              ))}
          </div>
      )}
    </div>
  );
}
